import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU, daysUntil } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Calendar, Clock, CheckCircle2, AlertCircle, Filter, Plus, Pencil,
  LayoutList, CalendarDays, ChevronLeft, ChevronRight, ListTodo, Save, Upload, Download, Sparkles
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TaskModal from '@/components/TaskModal';
import { ApplyTemplateModal, SaveAsTemplateModal } from '@/components/dashboard/TaskTemplatesLibrary';
import { BulkImportTasksDialog, ExportTasksDialog } from '@/components/tasks/BulkTasksDialogs';
import { MeetingNotesExtractorDialog } from '@/components/tasks/MeetingNotesExtractorDialog';
import { toast } from 'sonner';

export default function ClientWorkflow() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [entities, setEntities] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dueFilter, setDueFilter] = useState('all');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showMeetingExtractor, setShowMeetingExtractor] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !clientId) return;
      try {
        const [tasksRes, entRes, indRes] = await Promise.all([
          api.getWorkflow(token, clientId),
          api.getEntities(token, clientId),
          api.getIndividuals(token, clientId)
        ]);
        setTasks(tasksRes.data);
        setEntities(entRes.data);
        setIndividuals(indRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [token, clientId]);

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.name;
  };

  const getIndividualName = (individualId) => {
    const ind = individuals.find(i => i.id === individualId);
    return ind ? `${ind.firstName} ${ind.lastName}` : null;
  };

  const filteredTasks = tasks.filter(task => {
    if (dueFilter === 'all') return true;
    const days = daysUntil(task.dueDate);
    if (days === null) return false;
    switch (dueFilter) {
      case '7': return days >= 0 && days <= 7;
      case '14': return days >= 0 && days <= 14;
      case '30': return days >= 0 && days <= 30;
      default: return true;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'badge-verified';
      case 'In Progress': return 'badge-needs-review';
      case 'Pending': return 'bg-[#F0EBE6] text-[#5D524D]';
      default: return '';
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (editingTask) {
        // Edit existing task via API
        const response = await api.updateTask(token, clientId, editingTask.id, taskData);
        
        // Check if the task was archived (completed recurring task)
        if (response.data.archived) {
          // Remove the completed task and add the new recurring task if exists
          setTasks(prev => prev.filter(t => t.id !== editingTask.id));
          toast.success('Task completed and archived. New recurring task created.');
        } else {
          // Regular update
          setTasks(prev => prev.map(t => 
            t.id === editingTask.id ? response.data : t
          ));
          toast.success('Task updated successfully');
        }
      } else {
        // Create new task via API
        const response = await api.createTask(token, clientId, taskData);
        setTasks(prev => [...prev, response.data]);
        toast.success('Task created successfully');
      }
      
      // Refresh tasks to get any new recurring tasks
      const tasksRes = await api.getWorkflow(token, clientId);
      setTasks(tasksRes.data);
      
      setShowTaskModal(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Failed to save task:', error);
      toast.error('Failed to save task');
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  };

  const getTasksForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter(task => task.dueDate?.startsWith(dateStr));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === currentMonth.getMonth() && 
           today.getFullYear() === currentMonth.getFullYear();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up overflow-x-hidden" data-testid="workflow-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-xl sm:text-2xl text-[#2C2420]">Workflow & Key Dates</h1>
          <p className="text-sm text-[#5D524D]">Tasks and key dates for {client?.name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            size="sm"
            variant="outline"
            onClick={() => setShowSaveTemplate(true)}
            data-testid="save-as-template-btn"
            disabled={tasks.length === 0}
            className="h-8 text-xs sm:text-sm"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden sm:inline">Save as </span>Template
          </Button>
          <Button 
            size="sm"
            variant="outline"
            onClick={() => setShowApplyTemplate(true)}
            data-testid="apply-template-btn"
            className="h-8 text-xs sm:text-sm"
          >
            <ListTodo className="w-3.5 h-3.5 mr-1.5" />
            Apply
          </Button>
          <Button 
            size="sm"
            variant="outline"
            onClick={() => setShowMeetingExtractor(true)}
            data-testid="meeting-extractor-btn"
            className="h-8 text-xs sm:text-sm border-[#A86A3A]/40 text-[#A86A3A] hover:bg-[#F5EDE4]"
            title="Paste meeting notes — AI will draft the follow-up tasks"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden sm:inline">Notes → </span>Tasks (AI)
          </Button>
          <Button 
            size="sm"
            variant="outline"
            onClick={() => setShowBulkImport(true)}
            data-testid="bulk-import-tasks-btn"
            className="h-8 text-xs sm:text-sm"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden sm:inline">Bulk </span>Import
          </Button>
          <Button 
            size="sm"
            variant="outline"
            onClick={() => setShowExport(true)}
            data-testid="export-tasks-btn"
            className="h-8 text-xs sm:text-sm"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Export
          </Button>
          <Button 
            size="sm"
            className="caffeine-btn h-8 text-xs sm:text-sm" 
            onClick={handleCreateTask}
            data-testid="create-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </Button>
        </div>
      </div>

      {/* Filter and View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-1.5 shrink-0">
            <Filter className="w-4 h-4 text-[#8C8078]" />
            <span className="text-xs sm:text-sm text-[#5D524D]">Due:</span>
          </div>
          <Select value={dueFilter} onValueChange={setDueFilter}>
            <SelectTrigger className="w-[110px] sm:w-[140px] h-8 sm:h-9 text-xs sm:text-sm" data-testid="due-filter-select">
              <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tasks</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="14">14 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
          </SelectContent>
        </Select>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2 bg-[#F0EBE6] rounded-lg p-1">
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}
            data-testid="list-view-btn"
          >
            <LayoutList className="w-4 h-4 mr-1" />
            List
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('calendar')}
            className={viewMode === 'calendar' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}
            data-testid="calendar-view-btn"
          >
            <CalendarDays className="w-4 h-4 mr-1" />
            Calendar
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="caffeine-card p-3 sm:p-6 mb-6 overflow-x-auto" data-testid="calendar-view">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" onClick={prevMonth} className="caffeine-btn-ghost">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-4">
              <h2 className="font-heading text-xl text-[#2C2420]">{formatMonthYear(currentMonth)}</h2>
              <Button variant="ghost" size="sm" onClick={goToToday} className="caffeine-btn-ghost text-sm">
                Today
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={nextMonth} className="caffeine-btn-ghost">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-[#8C8078] py-2">
                {day}
              </div>
            ))}

            {/* Empty cells for days before month starts */}
            {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, idx) => (
              <div key={`empty-${idx}`} className="h-16 sm:h-24 bg-[#F9F7F5]/50 rounded" />
            ))}

            {/* Days of month */}
            {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, idx) => {
              const day = idx + 1;
              const dayTasks = getTasksForDate(day);
              const hasUrgent = dayTasks.some(t => t.status !== 'Completed');

              return (
                <div
                  key={day}
                  className={`h-16 sm:h-24 p-1 rounded border transition-colors ${
                    isToday(day)
                      ? 'border-[#4A3B32] bg-[#4A3B32]/5'
                      : 'border-[#E6E0D9] hover:border-[#C6A88F]'
                  }`}
                >
                  <div className={`text-xs font-medium mb-1 ${isToday(day) ? 'text-[#4A3B32]' : 'text-[#8C8078]'}`}>
                    {day}
                  </div>
                  <div className="space-y-0.5 overflow-hidden">
                    {dayTasks.slice(0, 2).map(task => (
                      <div
                        key={task.id}
                        className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer ${
                          task.status === 'Completed'
                            ? 'bg-[#6B8E6B]/20 text-[#6B8E6B]'
                            : task.status === 'In Progress'
                            ? 'bg-[#D99A6C]/20 text-[#D99A6C]'
                            : 'bg-[#4A3B32]/10 text-[#4A3B32]'
                        }`}
                        onClick={() => handleEditTask(task)}
                        title={task.title}
                      >
                        {task.title}
                      </div>
                    ))}
                    {dayTasks.length > 2 && (
                      <div className="text-xs text-[#8C8078] px-1">
                        +{dayTasks.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#E6E0D9]">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#4A3B32]/10" />
              <span className="text-xs text-[#8C8078]">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#D99A6C]/20" />
              <span className="text-xs text-[#8C8078]">In Progress</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#6B8E6B]/20" />
              <span className="text-xs text-[#8C8078]">Completed</span>
            </div>
          </div>
        </div>
      )}

      {/* Tasks List */}
      {viewMode === 'list' && filteredTasks.length > 0 && (
        <div className="space-y-4">
          {filteredTasks.map(task => {
            const days = daysUntil(task.dueDate);
            const isUrgent = days !== null && days <= 7 && days >= 0;
            
            return (
              <div 
                key={task.id} 
                className={`caffeine-card p-5 ${isUrgent ? 'border-l-4 border-l-[#D99A6C]' : ''}`}
                data-testid={`task-${task.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2 sm:gap-4 min-w-0">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      task.status === 'Completed' 
                        ? 'bg-[#6B8E6B]/10' 
                        : task.status === 'In Progress' 
                          ? 'bg-[#D99A6C]/10' 
                          : 'bg-[#F0EBE6]'
                    }`}>
                      {task.status === 'Completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-[#6B8E6B]" />
                      ) : isUrgent ? (
                        <AlertCircle className="w-5 h-5 text-[#D99A6C]" />
                      ) : (
                        <Calendar className="w-5 h-5 text-[#5D524D]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-sm sm:text-base text-[#2C2420] mb-1 truncate">{task.title}</h3>
                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-[#8C8078] flex-wrap">
                        <span>Owner: {task.owner}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          task.type === 'Key date' ? 'bg-[#4A3B32]/10 text-[#4A3B32]' : 'bg-[#6B8E6B]/10 text-[#6B8E6B]'
                        }`}>
                          {task.type}
                        </span>
                        {task.isRecurring && (
                          <span className="px-2 py-0.5 rounded text-xs bg-[#C6A88F]/20 text-[#4A3B32]" title={`Recurs ${task.recurringFrequency}`}>
                            ↻ {task.recurringFrequency}
                          </span>
                        )}
                      </div>
                      {(task.linkedEntityId || task.linkedIndividualId) && (
                        <div className="flex items-center gap-2 mt-2 text-sm">
                          {task.linkedEntityId && (
                            <span 
                              className="text-[#4A3B32] cursor-pointer hover:underline"
                              onClick={() => navigate(`/app/clients/${clientId}/entities/${task.linkedEntityId}`)}
                            >
                              {getEntityName(task.linkedEntityId)}
                            </span>
                          )}
                          {task.linkedIndividualId && (
                            <span 
                              className="text-[#4A3B32] cursor-pointer hover:underline"
                              onClick={() => navigate(`/app/clients/${clientId}/individuals/${task.linkedIndividualId}`)}
                            >
                              {getIndividualName(task.linkedIndividualId)}
                            </span>
                          )}
                        </div>
                      )}
                      {task.notes && (
                        <p className="text-sm text-[#5D524D] mt-2">{task.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex items-start gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className={`w-4 h-4 ${isUrgent ? 'text-[#D99A6C]' : 'text-[#8C8078]'}`} />
                        <span className={`font-medium ${isUrgent ? 'text-[#D99A6C]' : 'text-[#2C2420]'}`}>
                          {formatDateAU(task.dueDate)}
                        </span>
                      </div>
                      {days !== null && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          days <= 7 ? 'badge-needs-review' : 'bg-[#F0EBE6] text-[#5D524D]'
                        }`}>
                          {days === 0 ? 'Due today' : days === 1 ? '1 day' : `${days} days`}
                        </span>
                      )}
                      <div className="mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="caffeine-btn-ghost"
                      onClick={() => handleEditTask(task)}
                      data-testid={`edit-task-${task.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {viewMode === 'list' && filteredTasks.length === 0 && (
        <div className="empty-state">
          <Calendar className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No tasks found</p>
          <p className="text-sm">No workflow items match the current filter</p>
          <Button 
            className="caffeine-btn mt-4" 
            onClick={handleCreateTask}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Task
          </Button>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          task={editingTask}
          entities={entities}
          individuals={individuals}
          onClose={() => {
            setShowTaskModal(false);
            setEditingTask(null);
          }}
          onSave={handleSaveTask}
        />
      )}

      {/* Apply Template Modal */}
      <ApplyTemplateModal
        open={showApplyTemplate}
        onClose={(applied) => {
          setShowApplyTemplate(false);
          if (applied) {
            api.getWorkflow(token, clientId).then(res => setTasks(res.data)).catch(() => {});
          }
        }}
        contextType="client"
        contextId={clientId}
        contextName={client?.name}
      />

      {/* Save as Template Modal */}
      <SaveAsTemplateModal
        open={showSaveTemplate}
        onClose={(saved) => {
          setShowSaveTemplate(false);
        }}
        contextType="client"
        contextId={clientId}
        contextName={client?.name}
      />

      {/* Bulk Import Tasks Dialog */}
      <BulkImportTasksDialog
        open={showBulkImport}
        onOpenChange={setShowBulkImport}
        clientId={clientId}
        onImported={() => {
          api.getWorkflow(token, clientId).then(res => setTasks(res.data)).catch(() => {});
        }}
      />

      {/* Export Tasks Dialog */}
      <ExportTasksDialog
        open={showExport}
        onOpenChange={setShowExport}
        clientId={clientId}
        clientName={client?.name}
      />

      {/* Meeting Notes → Tasks AI Extractor */}
      <MeetingNotesExtractorDialog
        open={showMeetingExtractor}
        onOpenChange={setShowMeetingExtractor}
        clientId={clientId}
        onCreated={() => {
          api.getWorkflow(token, clientId).then(res => setTasks(res.data)).catch(() => {});
        }}
      />
    </div>
  );
}
